import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { cloneElement, isValidElement, type ReactElement, type ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { cx, IconButton, ScrollEdge, SearchField, useIsDesktop } from '../../ui';
import { useHistoryPosition } from '../nav/historyPosition';
import { settingsPrevious } from '../nav/settingsStack';
import { useBack } from '../nav/useBack';
import { lastAppPathAtom, moreSheetOpenAtom, sidebarCollapsedAtom } from './shellState';
import { PhoneBarActions, ToolbarActions, ToolbarGroup } from './WindowToolbar';

/**
 * THE CONTROL THAT SHOWS AND HIDES THE PANE (wave 5, package A3, decision 9 and draft
 * section 9). It has TWO seats, and which one it takes is the state of the thing it acts on.
 *
 * WITH THE PANE OPEN it stands in the pane's own band, at the trailing edge:
 * kit: `Apple macOS 27 UI Kit.sketch / Windows/Light/Unified Toolbar + Title + Sidebar/Active`,
 * Left Pane/Titlebar and Toolbar/Sidebar Toggle, 36 by 36 at (212,8) of a 256 pane. That is
 * where `Sidebar` renders it, and it is why it is exported from this file rather than written
 * twice: one control, one construction, one label rule, two places it can stand.
 *
 * WITH THE PANE CLOSED there is no band to stand in, so it takes the window toolbar before
 * the navigation group. The kit ships no collapsed window at all, so that placement is a
 * derived adaptation rather than a master (draft section 9, "Collapsed sidebar, E DERIVED"):
 * hig: https://developer.apple.com/design/human-interface-guidelines/toolbars puts a window's
 * own controls at the leading edge, and the group beside it stays what the kit says it is -
 * navigation, and nothing else.
 *
 * THE SYMBOL IS THE GENERATED `sidebar` ONE (owner, 2026-09-10): a window frame with a
 * leading pane, flat in the button's ink, never on a tile.
 */
export function SidebarToggle() {
  const [collapsed, setCollapsed] = useAtom(sidebarCollapsedAtom);
  return (
    <IconButton
      glyph="sidebar"
      label={collapsed ? 'Show sidebar' : 'Hide sidebar'}
      aria-expanded={!collapsed}
      data-sidebar-toggle={collapsed ? 'collapsed' : 'expanded'}
      onClick={() => setCollapsed(!collapsed)}
    />
  );
}

/**
 * S1-4: the window title, at most three groups, scoped search and one prominent action.
 * The header itself has no material; the groups own their glass and the scroll edge its tint.
 * Existing page actions keep their handlers. S1-7 gives the phone its large title.
 */
export interface PageHeaderProps {
  readonly title: string;
  readonly subtitle?: string | undefined;
  readonly actions?: ReactNode | undefined;
  readonly className?: string | undefined;
  readonly leading?: ReactNode;
  /**
   * SH-1: THE TOOLBAR'S CENTRE AREA. A unified toolbar has three places, not two - leading,
   * centre, trailing - and a screen's SECTIONS belong in the middle one: the pane's
   * segmented control (Members: Members, Access roles), which the spec puts there
   * (`crm-design/spec/screens/settings-shell.md` section 2 item 3 and
   * `settings-pane.md` section 2 item 1). Until this slot existed a pane had to hand its
   * control in with its actions, where `ToolbarActions` swept it into the page-actions
   * capsule beside the prominent button - one long capsule holding a control and a verb.
   *
   * The slot takes the control AS THE PAGE BUILT IT and gives it a place, not a skin: the
   * kit's toolbar item height around it and nothing else. On the phone there is no centre
   * area, so it becomes the row directly under the bar, which is where iOS puts a scope
   * control on a pushed screen.
   */
  readonly centre?: ReactNode;
  /**
   * WHICH BAR THE PHONE DRAWS, AND THE PAGE OWNS IT (wave 4 round 2, item 12). iOS has two:
   * kit: `Apple iOS 27 UI Kit.sketch / Toolbars/44pt/Light/iPhone/Standard - Large`, height 105px for a stack root.
   * kit: `Apple iOS 27 UI Kit.sketch / Toolbars/44pt/Light/iPhone/Standard - Title`, height 54px for a pushed screen.
   * Which of the two a screen is cannot be
   * derived from its URL - `/reports/:id` is a pushed screen and `/tasks` is a root, and
   * both are one path segment deep - so the shell was reading a hard-coded list of route
   * families and getting the report wrong: `crm-design/spec/screens/reports.md` part 3 item
   * 2 says "A report: pushed screen with the standard title" and it drew the large one.
   *
   * A page states what it is; the route list below stays the default for the pages that
   * state nothing, so no page has to change to keep what it has.
   */
  readonly bar?: 'large' | 'standard' | undefined;
  readonly search?: {
    readonly value: string;
    readonly onChange: (value: string) => void;
    readonly placeholder: string;
  };
  /**
   * THE TITLE STANDS IN THE CONTENT (redesign register L3; fe-foundation). Ben's five pages
   * draw a large title at the top of their content (`ui/LargeTitle`), so at desktop widths the
   * toolbar leaves its title text out and keeps the title as its accessible name; the page
   * then has one `h1`, the content's. The phone keeps this header's own large title (P6), and
   * every page that does not pass the option is unchanged.
   */
  readonly largeTitle?: boolean | undefined;
}

export function PageHeader({
  title,
  subtitle,
  actions,
  className,
  leading,
  centre,
  search,
  bar,
  largeTitle = false,
}: PageHeaderProps) {
  const collapsed = useAtomValue(sidebarCollapsedAtom);
  const lastAppPath = useAtomValue(lastAppPathAtom);
  const openMore = useSetAtom(moreSheetOpenAtom);
  const location = useLocation();
  const desktop = useIsDesktop();
  const settings = location.pathname.startsWith('/settings');
  /**
   * ONE BACK CONTROL, AND IT RETRACES THE STACK (SH-A8 round 3, owner-48, owner-56).
   *
   * The capsule used to link to one fixed place - `navigate(lastAppPath)` on every settings
   * screen - so from a role's, a webhook's or an object's detail it left settings instead of
   * popping the pane, and each of those panes had to draw a back link of its own to make up
   * for it. It is now the same control the panes' links are: `useBack()` pops what the person
   * walked, and the settings hierarchy (`app/nav/settingsStack.ts`) supplies the two things a
   * pop cannot - the previous screen's title, which is what Apple's back button is called, and
   * the screen to seed when a deep link left nothing to pop.
   */
  const previous = settings ? settingsPrevious(location.pathname) : null;
  /**
   * THE PHONE'S SETTINGS LIST IS A SHEET, NOT A ROUTE (SH-3). Below the breakpoint the screen
   * behind a pane is the "More" list, which `MobileNav` opens over the pane, so there is
   * nothing to navigate to and the control opens it - exactly what the phone's own chevron
   * did before this round. A pushed pane is a push on both, and goes back the same way.
   */
  const toSheet = !desktop && previous !== null && previous.screen !== 'pushed';
  const back = useBack(previous === null || toSheet ? undefined : (previous.to ?? lastAppPath));
  /**
   * THE WINDOW'S OWN PAIR (wave 4 round 1, C2-7). Finder, App Store and System Settings all
   * carry back AND forward at the far leading edge of every window, which is also where the
   * HIG puts them: "Navigation controls, like back and forward" are one of the three things
   * a toolbar holds, and "Elements that let people return to the previous document and show
   * or hide a sidebar appear at the far leading edge" (hig:
   * https://developer.apple.com/design/human-interface-guidelines/toolbars).
   *
   * WHAT IT IS CALLED IS STILL THE SETTINGS LADDER'S ANSWER, because Apple's back button
   * carries the previous screen's title and `app/nav/settingsStack.ts` is what knows it -
   * so the pair REPLACES the ladder's capsule rather than standing beside it, and the two
   * seeded answers a deep link needs (`useBack`) stay exactly where they were. Everywhere
   * else the control is the window's own "Back", enabled while the stack has something
   * behind it.
   */
  const position = useHistoryPosition();
  const navigate = useNavigate();
  const record = /^\/objects\/[^/]+\/records\/[^/]+$/.test(location.pathname);
  /**
   * SH-3: A SETTINGS PANE IS A PUSHED SCREEN ON THE PHONE, like the record.
   *
   * The phone reaches a pane from the More list and lands on it, so the pane is the second
   * screen of a stack and carries the standard title beside its back control.
   * kit: `Apple iOS 27 UI Kit.sketch / Toolbars/44pt/Light/iPhone/Standard - Title`, height 54px.
   * `crm-design/spec/screens/settings-shell.md` section 3 item 2 requires this bar. Round 2 built the
   * record's half of this; every pane still rendered as a stack ROOT, with the large title
   * and its action line above it. Same construction, one more route family.
   */
  /**
   * THE DEFAULT IS THE ROUTE LIST AND THE PAGE'S OWN WORD BEATS IT (item 12). A report's
   * detail joins the list because the spec puts it there and because no page passes the
   * prop yet; every other family is exactly what it was.
   */
  const report = /^\/reports\/[^/]+$/.test(location.pathname);
  const pushed = bar === undefined ? record || settings || report : bar === 'standard';
  useEffect(() => {
    document.title = `${title} | OFMOS`;
  }, [title]);
  /**
   * THE SAME CONTROL IN BOTH BARS. The window toolbar puts it inside the navigation group
   * with the sidebar toggle and the phone's standard bar carries it alone, so it is placed
   * by the breakpoint's own classes the way it was before; what it says and where it goes is
   * one construction, computed once, and never a target belonging to a route.
   */
  const goBack = () => {
    if (toSheet) {
      openMore(true);
      return;
    }
    back.goBack();
  };
  const backControl =
    previous === null ? null : (
      <IconButton
        data-toolbar-item="back"
        icon="chevron-left"
        label={toSheet ? 'Settings' : previous.label}
        onClick={goBack}
      />
    );
  /**
   * THE PAIR IS ALWAYS THERE AND SAYS WHEN IT LEADS NOWHERE, which is the state Apple ships
   * rather than a control that appears and disappears: "The system disables backward
   * navigation controls when the stack is empty and the root view ... is visible"
   * (https://developer.apple.com/documentation/swiftui/navigationstack). The kit has the
   * disabled state to draw it with (`Titlebars and Toolbars/.../Button Group Buttons/
   * Active/XL/4 - Disabled`) and no state for an item that is not there.
   */
  const windowBack =
    previous !== null ? (
      backControl
    ) : (
      <IconButton
        data-toolbar-item="back"
        icon="chevron-left"
        label="Go back"
        disabled={!position.canGoBack}
        onClick={goBack}
      />
    );
  const windowForward = (
    <IconButton
      data-toolbar-item="forward"
      icon="chevron-right"
      label="Go forward"
      disabled={!position.canGoForward}
      onClick={() => void navigate(1)}
    />
  );
  /**
   * A PAGE THAT BRINGS ITS OWN BACK CONTROL IS THE PAIR'S BACK (wave 4 round 1, C2-7,
   * ledger S1-W4-5). The record hands one in through `leading` - `BackButton` on
   * `useBack()` with the object list as its cold-open fallback - and the window's own
   * control cannot answer that last part: `useBack` with no fallback returns false when a
   * document was loaded cold, so replacing it would have taken a real behaviour away
   * rather than moved it. It stands where Apple puts a back control instead, first in the
   * navigation group at the far leading edge, and carries the pair's mark so a review
   * reads one control rather than two. Apple's back button is named after the screen
   * behind it, which is what that control already says.
   *
   * ONE COPY, PLACED BY THE BREAKPOINT rather than by a class: at 375 the node belongs to
   * the phone's own bar, and rendering it in both places would put two identical buttons
   * in the accessibility tree.
   */
  const pageBack =
    desktop && isValidElement(leading)
      ? cloneElement(leading as ReactElement<Record<string, unknown>>, {
          'data-toolbar-item': 'back',
          /* AND IT DRAWS THE PAIR'S OWN CONTROL (wave 4 round 2, item 0b): a chevron, the
             screen behind it as the accessible name, the verb phrase as the help tag. */
          inPair: true,
        })
      : null;
  return (
    <>
      <header
        // CONTENT PASSES UNDER THIS BAR BY DESIGN, so `measure`'s covered-text rule is told
        // so once, here, rather than by every paragraph that ever scrolls beneath it. The
        // ignore is listed in the artifact, which is what keeps it a decision (D4).
        data-measure-ignore="covered-text:the scroll edge, and content passing UNDER this bar is the mechanism working (docs/ios26-mechanisms.md)"
        {...(largeTitle && desktop ? { 'aria-label': title } : {})}
        className={cx(
          'shell-page-header relative isolate layer-sticky md:sticky md:top-0',
          pushed && 'shell-pushed-header',
          className,
        )}
      >
        {/*
          THE BAR'S OWN SCROLL EDGE, AND THE DESKTOP TAKES THE KIT'S HARD MASTER (wave 4
          round 2 of the glass package, finding F1; it supersedes S1-W4-9, which chose soft
          at every width).

          THE KIT DOES NOT LEAVE THE CHOICE OPEN, IT INSTANCES IT. `Windows/Dark/Unified
          Toolbar + Title + Sidebar/Active` carries the instance `Scroll Edge Effect` at
          (0,0,584,52) inside the `Right Pane`, under the toolbar group and over the scroll
          view, and the master it instances is the HARD one; iOS instances the soft master
          under its bars (`Examples/Toolbar/Bottom/iPhone - Controls (Light)`). So the two
          styles are not a preference between two readings of one sentence - they are two
          platforms, and this bar is the Mac one
          (`crm-design/research/CRM-WAVE4-KIT-LOOKUP-2026-09-12.md` section 5). wwdc:
          https://developer.apple.com/videos/play/wwdc2025/356/ at 11:18 - "Hard is mostly
          used on macOS. It creates a stronger, more opaque boundary, ideal for interactive
          text, controls without backgrounds, or pinned table headers that need extra
          clarity"; hig:
          https://developer.apple.com/design/human-interface-guidelines/scroll-views -
          "Prefer the automatic scroll edge effect style", one effect per view, never mixed.

          WHAT THE SOFT MASTER COST, WHICH IS WHY THIS CHANGED. The argument for soft was
          that every item in this bar is a glass capsule with a background of its own. One
          thing in the bar is not: the WINDOW TITLE. At 1440 over scrolled content it stood
          on sharp row text and collided with it - "Tasks" over "Reed Studio - Agency ...
          Lena Roth" in
          `_screens-g1-glass/w4-round-1/chromium/tasks-toolbar-scrolled-1440-dark-chromium.png`
          - and a window title is exactly the "interactive text ... that needs extra
          clarity" the WWDC sentence hands to the hard style. Measured on the running build,
          the strip the title stands on read a standard deviation of 2.862 light and 2.500
          dark with the soft effect over it against 3.816 and 3.339 bare; the hard master's
          flat tint at 0.85 and 0.55 cannot leave more than 0.569 and 1.506 of that
          (check (m2), `packages/e2e/review/behaviour.spec.ts`).

          THE CAPSULES KEEP THEIR BACKDROP, DIMMED. A hard edge is a 6px background blur
          under a flat tint, not an opaque wall: the glass on it bends what the tint leaves,
          which is Apple's own construction and the reason the owner reads a WEAKER bend at
          Apple than in this product (C2-21, owner-99 "die Brechung wirkt staerker als bei
          Apple somehow"). Check (m4) holds that bend above wave 3's "nothing bends".

          THE PHONE KEEPS SOFT, from the same hook the rest of this header decides phone
          against desktop with.
        */}
        <div className="shell-toolbar-edge">
          <ScrollEdge host="window-toolbar" style={desktop ? 'hard' : 'soft'} />
        </div>
        <div className="shell-toolbar-leading">
          {/*
            THE TOGGLE IS HERE ONLY WHILE THE PANE IS CLOSED (package A3, decision 9). Open,
            it stands in the pane's own band, which is where the kit's assembled window puts
            it; closed, there is no band, so it leads the bar. The group itself holds what the
            kit says a navigation group holds - back and forward, and nothing else.
            kit: `Apple macOS 27 UI Kit.sketch / Windows/Light/Unified Toolbar + Title + Sidebar/Active`,
            Right Pane/Titlebar and Toolbar/Navigation, 73x36 for two buttons and a separator.
          */}
          {collapsed && (
            <div className="hidden md:flex">
              <SidebarToggle />
            </div>
          )}
          <div className="hidden md:flex">
            <ToolbarGroup label="Window navigation" navigation>
              {pageBack ?? windowBack}
              {/* The kit's separator between the two segments (round 2, F2). */}
              <span className="shell-toolbar-separator" aria-hidden="true" />
              {windowForward}
            </ToolbarGroup>
          </div>
          {/*
            THE PHONE'S PUSHED BAR CARRIES THE SAME CONTROL (SH-3, round 3). The chevron
            alone, the way the kit's own pushed bar draws it and never a worded label (spec
            settings-shell.md section 2 item 3, rulebook N8); it is hidden with a class
            rather than a viewport hook so a states-page specimen at 1440 still renders the
            phone construction it is a specimen of. What changed this round is where it
            leads: it opened the More list from every settings screen, a pushed pane
            included, and a pushed pane's screen behind it is its own list.
          */}
          {backControl !== null && <div className="md:hidden">{backControl}</div>}
          {/*
            AND THE PAGE'S OWN LEADING NODE IS THE PHONE'S (wave 4 round 1, C2-7). It is
            where a pushed screen keeps its back affordance below the breakpoint - the
            record's capsule to its object list - and above it the window's pair IS that
            control, so drawing both would put two back controls a centimetre apart in the
            same bar. The node stays in the DOM at every width and is hidden by the same
            class the phone's own chevron uses, because a specimen at 1440 is a picture of
            the phone construction and has to keep rendering it.
          */}
          {!desktop && leading !== undefined && leading}
          {/* The title's own rule owns its overflow: one line with an ellipsis as a window
              title, wrapping as a phone large title (index.css). */}
          {largeTitle && desktop ? (
            /* The slot keeps its place in the row (the title is the elastic part of the
               leading slot, index.css) and carries no text: the content's large title is the
               page's heading. */
            <span className="shell-window-title" data-large-title-slot="" />
          ) : (
            <h1 className="shell-window-title">{title}</h1>
          )}
        </div>
        {centre !== undefined && <div className="shell-toolbar-centre">{centre}</div>}
        {/*
          THE ACTION ROW EXISTS ONLY WHERE IT HAS SOMETHING IN IT (round 3, finding O7).
          Its three children are desktop-only except `actions`, so on a phone screen with
          no actions - a pushed record - it rendered a zero-height flex line and the row
          gap above it, under a bar that was already reserving the large title's height.
          That is the empty band between the record's title row and "Agency".
        */}
        {(desktop || actions !== undefined) && (
          <div className="shell-toolbar-actions">
            {/*
              THE HISTORY GROUP IS GONE UNTIL THERE IS HISTORY (SH-A12). It held one
              permanently disabled Undo capsule - S1 reserved the place and the
              reservation shipped, so every screen in the product carried a control that
              could never do anything (owner-03 and every other toolbar). `ToolbarUndo`
              now renders nothing without an `onUndo`, and an empty capsule around
              nothing would have been the same defect in a thinner shape, so the group
              goes with it. The action layer brings both back together.
            */}
            {/*
              AND THE PHONE DRAWS THE SAME ITEMS (wave 5 round 3, C2-2). This line used to
              render the page's actions verbatim below the breakpoint while the desktop
              handed them to the bar's own construction, which is why one add control read
              "+ Add" on the phone and a bare plus on the Mac. `PhoneBarActions` is that
              construction's symbol half and nothing else - the groups, the measurement and
              the More menu stay the window frame's.
            */}
            {actions !== undefined &&
              (desktop ? (
                <ToolbarActions actions={actions} />
              ) : (
                <PhoneBarActions actions={actions} />
              ))}
            {/*
              THE BAR CARRIES THE SCREEN'S OWN SEARCH AND NOTHING ELSE (wave 4 round 1,
              C2-4 and C2-18). A screen that scopes a search gets the kit's toolbar field
              (`Titlebars and Toolbars/.../XL/Search/Active/Placeholder`); the GLOBAL search
              used to stand here as well, as a second capsule reading "Search all records"
              that opened a centred palette from a screen whose own list it did not search.
              It is one control now and it is in the sidebar, where the field and its
              results are the same construction - which is also what took the "two search
              fields" question off the list.
            */}
            {search !== undefined && (
              <div className="hidden md:flex">
                <SearchField
                  context="toolbar"
                  className="shell-search-toolbar"
                  label={search.placeholder}
                  value={search.value}
                  onChange={(event) => search.onChange(event.target.value)}
                />
              </div>
            )}
          </div>
        )}
      </header>
      {subtitle !== undefined && <p className="py-2 text-sm text-ink-muted">{subtitle}</p>}
    </>
  );
}
